import { useEffect, useRef, useState } from 'react';
import {
  FilterBar,
  ListEmpty,
  NumberedPager,
  useListUrlState,
  rangeForPreset,
  windowLabel,
  inRange,
  applySearch,
  sortBy,
  paginate,
  DEFAULT_HISTORY_PRESET,
  type HistoryDatePreset,
} from '@/components/lists';
import type { Card } from '../types';
import { BLACK, cardFaxPage, cardWhereToFind, money, paymentsWord, pciDone, reasonPhrase } from '../utils';
import { DC_CARD_MONTH } from '../fixtures';
import { getCardRate, monthCardFees } from '../store';
import { Link } from 'react-router-dom';

interface ListProps {
  toCharge: Card[];
  couldnt: Card[];
  /** The big Virtual cards › Charged history (§9). */
  chargedHistory: Card[];
  selectedId?: string;
  onSelect: (id: string) => void;
}

const PAGE_SIZE = 50;

function cardText(c: Card): string {
  return `${c.payer} ${money(c.amount)} ${String(Math.round(c.amount))} ${c.last4} ${c.chargedApproval ?? ''} ${c.claims.map((cl) => cl.patient).join(' ')}`;
}

function CardRow({ c, selected, onSelect }: { c: Card; selected: boolean; onSelect: (id: string) => void }) {
  const badge = c.status === 'to_charge' ? <span className="dc-badge b-warn">To charge</span>
    : c.status === 'charged' ? <span className="dc-badge b-ok">Charged</span>
      : <span className="dc-badge b-warn">Couldn’t charge</span>;
  const claimsWord = `${String(c.claims.length)} claim${c.claims.length === 1 ? '' : 's'}`;
  const meta = c.status === 'couldnt_charge' ? `${claimsWord} · ELVA asked ${c.payer} to pay another way`
    : c.status === 'charged' ? `${claimsWord} · ${c.chargedDate ? `charged ${c.chargedDate}` : 'charged'}`
    : `${claimsWord} · ${c.receivedDisplay.replace('Received by fax ', 'received ').replace('Received ', 'received ')} · ${c.expiresDisplay}`;
  return (
    <button type="button" className={`dc-row${selected ? ' on' : ''}`} aria-current={selected || undefined} onClick={() => { onSelect(c.id); }}>
      <span className="dc-rtop" style={{ alignItems: 'baseline' }}><strong style={{ fontSize: 15, fontWeight: 600 }}>{c.payer}</strong><span className="num" style={{ fontWeight: 600 }}>{money(c.amount)}</span></span>
      <span className="dc-meta" style={{ display: 'block', marginTop: 2 }}>{meta}</span>
      <span style={{ display: 'block', marginTop: 6 }}>{badge}</span>
    </button>
  );
}

export function CardsList({ toCharge, couldnt, chargedHistory, selectedId, onSelect }: ListProps) {
  const [s, patch] = useListUrlState({ q: '', range: DEFAULT_HISTORY_PRESET, page: '1' });
  const q = s.q;
  const preset = s.range as HistoryDatePreset;
  const page = Math.max(1, Number.parseInt(s.page, 10) || 1);
  const win = rangeForPreset(preset);

  const toChargeTotal = toCharge.reduce((sum, c) => sum + c.amount, 0);

  // Charged history: search → month window → newest first → page.
  const searched = applySearch(chargedHistory, q, cardText);
  const inWindow = searched.filter((c) => inRange(c.chargedIso ?? '', win));
  const sorted = sortBy(inWindow, (c) => c.chargedIso ?? '', -1);
  const pageRows = paginate(sorted, page, PAGE_SIZE);

  return (
    <aside className="dc-list" aria-label="Virtual cards">
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
        <div><div className="dc-meta">October</div><div className="num" style={{ fontSize: 20, fontWeight: 700 }}>{DC_CARD_MONTH.count} cards · {money(DC_CARD_MONTH.total)}</div></div>
        <div><div className="dc-meta">Card fees, at your {getCardRate().toFixed(1)}%</div><div className="num" style={{ fontSize: 20, fontWeight: 700 }}>about {money(monthCardFees())}</div></div>
      </div>

      {toCharge.length > 0 ? (
        <>
          <h2 className="dc-grp">To charge · {toCharge.length} · {money(toChargeTotal)}</h2>
          {toCharge.map((c) => <CardRow key={c.id} c={c} selected={selectedId === c.id} onSelect={onSelect} />)}
        </>
      ) : null}

      {couldnt.length > 0 ? (
        <>
          <h2 className="dc-grp" style={{ color: 'var(--warning)' }}>Couldn’t charge · {couldnt.length}</h2>
          {couldnt.map((c) => <CardRow key={c.id} c={c} selected={selectedId === c.id} onSelect={onSelect} />)}
        </>
      ) : null}

      <h2 className="dc-grp">Charged · {windowLabel(preset)}</h2>
      <div className="dc-histfilters">
        <FilterBar
          search={q}
          onSearch={(v) => { patch({ q: v, page: '1' }); }}
          searchPlaceholder="Search payer, patient, amount or approval code"
          dateRange={preset}
          onDateRange={(v) => { patch({ range: v, page: '1' }); }}
        />
      </div>

      {pageRows.length === 0 ? (
        <ListEmpty
          query={q.trim() === '' ? undefined : q}
          windowLabel={windowLabel(preset)}
          {...(q.trim() !== '' && preset !== 'all-time'
            ? { onSearchAllTime: () => { patch({ range: 'all-time', page: '1' }); } }
            : {})}
          {...(q.trim() === '' ? { message: `No cards were charged in ${windowLabel(preset)}.` } : {})}
        />
      ) : (
        <>
          {pageRows.map((c) => <CardRow key={c.id} c={c} selected={selectedId === c.id} onSelect={onSelect} />)}
          <div className="dc-histfoot">
            <NumberedPager
              page={page}
              pageSize={PAGE_SIZE}
              total={sorted.length}
              onPage={(p) => { patch({ page: String(p) }); }}
            />
          </div>
        </>
      )}
    </aside>
  );
}

interface DetailProps {
  card: Card;
  onMarkCharged: (approval: string) => void;
  onCouldntCharge: () => void;
  onOpenFax: () => void;
  onAskEft: (payer: string) => void;
  onBack?: () => void;
}

/**
 * The faxed card page as a thumbnail. Always the redacted copy (D216): the number is solid
 * bars ending in the last 4, the security code is a bar. A page ELVA couldn't black out is
 * not kept, so there is no thumbnail, only where to find it in the fax inbox.
 */
function CardFax({ card, onOpenFax }: { card: Card; onOpenFax: () => void }) {
  // D252: by default no card page is kept, so no thumbnail. `?pci=done` brings the redacted copy back.
  const dropped = !pciDone();
  if (dropped || card.pageKept === false) {
    const src = card.source;
    return (
      <section className="dc-card" style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <strong>{src.kind === 'fax' ? 'The fax' : `${card.payer}’s portal`}</strong>
        <p style={{ margin: 0, fontSize: 14.5 }}>
          {dropped
            ? src.kind === 'fax'
              ? `ELVA doesn’t keep virtual-card pages yet. Charge it from your fax: the fax from ${card.payer}, ${src.time}.`
              : `ELVA doesn’t keep virtual-card pages yet. Charge it from ${card.payer}’s portal › Payments · ${src.ref}.`
            : `ELVA didn’t keep this page because it couldn’t black out the card number. Open it in your fax inbox: the fax from ${card.payer}, ${src.kind === 'fax' ? src.time : 'today'}.`}
        </p>
        <span className="dc-meta">Amount, payer and claims were read before the page was dropped, so the card can still be charged and posted.</span>
      </section>
    );
  }
  const page = cardFaxPage(card);
  const [head, payee, amount] = page.text.split('\n');
  return (
    <section className="dc-card" style={{ padding: '16px 18px', display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div className="dc-scan" role="img" aria-label={page.alt} style={{ fontSize: 12, lineHeight: 1.6 }}>
        {`${head ?? ''}\n${payee ?? ''}\n${amount ?? ''}\nCARD NUMBER: `}
        <span className="dc-redact">{`${BLACK.repeat(4)} ${BLACK.repeat(4)} ${BLACK.repeat(4)}`}</span>
        {` ${card.last4}\nEXP: ${card.expires}   CVV: `}
        <span className="dc-redact">{BLACK.repeat(3)}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
        <strong>The fax</strong>
        <span className="dc-meta">Card details removed when the fax arrived.</span>
        <button type="button" className="dc-btn dc-btn-ghost" style={{ padding: 0, minHeight: 32 }} onClick={onOpenFax}>Open the full page</button>
      </div>
    </section>
  );
}

export function CardDetail({ card, onMarkCharged, onCouldntCharge, onOpenFax, onAskEft, onBack }: DetailProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [approval, setApproval] = useState('');

  useEffect(() => {
    headingRef.current?.focus();
    setApproval('');
  }, [card.id]);

  const n = card.claims.length;
  const reasonId = `dc-charge-reason-${card.id}`;
  const approvalTyped = approval.trim() !== '';
  const couldnt = card.couldnt;

  return (
    <main className="dc-detail">
      {onBack ? <button type="button" className="dc-back" onClick={onBack}>← All cards</button> : null}
      <div className="dc-detail-inner">
        <section style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div>
            <span className="dc-kind">Virtual card</span>
            <h2 className="dc-doctitle" tabIndex={-1} ref={headingRef}>{card.payer} · {money(card.amount)}</h2>
            <div className="dc-meta" style={{ marginTop: 4 }}>{card.receivedDisplay} · {card.expiresDisplay} · pays {n} claim{n === 1 ? '' : 's'}</div>
          </div>
        </section>

        {card.status === 'couldnt_charge' && couldnt ? (
          <>
            <section className="dc-card dc-didcard" style={{ borderColor: 'var(--warn-border)' }}>
              <div className="dc-didhead" style={{ background: 'var(--wsoft)' }}>
                <span className="dc-badge b-warn" style={{ background: '#fff' }}>Couldn’t charge</span>
                <h3 className="dc-h3" style={{ color: '#5c3c00' }}>{card.payer} · {money(card.amount)} · {reasonPhrase(couldnt.reason, couldnt.note)}</h3>
              </div>
              <div className="dc-try" style={{ gridTemplateColumns: '84px minmax(0, 1fr)' }}>
                <span className="t">{couldnt.markedAt}</span>
                <span>
                  {couldnt.markedBy === 'ELVA'
                    ? 'ELVA marked it couldn’t charge, because the card expired before anyone charged it.'
                    : `${couldnt.markedBy} marked it couldn’t charge.`}
                  {couldnt.reason === 'other' && couldnt.note ? ` Note: ${couldnt.note}` : ''}
                </span>
              </div>
              <div className="dc-try" style={{ gridTemplateColumns: '84px minmax(0, 1fr)' }}>
                <span className="t">{couldnt.callAt}</span>
                <span>ELVA called {card.payer} · reference {couldnt.reference} · {couldnt.payerSaid}</span>
              </div>
              <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', fontWeight: 600 }}>Nothing more for you to do.</div>
            </section>
            <div className="dc-meta">
              The {n} claim{n === 1 ? ' is' : 's are'} back in <Link to="/ar" style={{ color: 'var(--primary)' }}>Insurance A/R</Link> as unpaid, with the reference.
            </div>
            <CardFax card={card} onOpenFax={onOpenFax} />
          </>
        ) : card.status === 'charged' ? (
          <>
            <section className="dc-card dc-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="dc-tick"><span className="dc-ok" aria-hidden="true">✓</span><span>{card.chargedBy}.</span></div>
              <dl className="dc-kv" style={{ marginTop: 4 }}>
                <dt>Card</dt><dd className="dc-card-num">··{card.last4}</dd>
                <dt>Approval code</dt><dd className="num">{card.chargedApproval}</dd>
              </dl>
              <div className="dc-meta">ELVA posted {paymentsWord(n)}. It keeps only the last 4 digits and the approval code.</div>
              <h3 className="dc-h3" style={{ marginTop: 6 }}>Payments posted</h3>
              {card.claims.map((cl, i) => (
                <div key={`pc-${String(i)}`} className="dc-payrow" style={{ padding: '8px 0' }}><span>{cl.patient} · {cl.visit}</span><strong className="num">{money(cl.amount)}</strong></div>
              ))}
            </section>
            <CardFax card={card} onOpenFax={onOpenFax} />
          </>
        ) : (
          <>
            {/* where to find the card */}
            <section className="dc-card dc-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h3 className="dc-h3" style={{ fontSize: 17 }}>Where to find the card</h3>
              <dl className="dc-kv">
                <dt>Card</dt><dd>Ending {card.last4} · expires {card.expires}</dd>
                <dt>Amount to charge</dt><dd className="num">{money(card.amount)}, exactly</dd>
                <dt>Where to find it</dt><dd>{cardWhereToFind(card)}</dd>
              </dl>
              <div className="dc-meta">
                {pciDone()
                  ? `ELVA removed the card number and security code when the ${card.source.kind === 'fax' ? 'fax' : 'document'} arrived, and doesn’t keep them. Charge the card from the original.`
                  : `ELVA doesn’t keep the card number, the security code or the card page. Charge the card from the original.`}
              </div>
            </section>

            {/* what it pays */}
            <section className="dc-card" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '16px 18px 8px' }}><h3 className="dc-h3">What it pays</h3></div>
              {card.claims.map((cl, i) => (
                <div key={`wp-${String(i)}`} className="dc-payrow"><span>{cl.patient} · {cl.visit}</span><strong className="num">{money(cl.amount)}</strong></div>
              ))}
              <div className="dc-meta" style={{ padding: '10px 18px 14px', borderTop: '1px solid var(--border)' }}>ELVA posts {paymentsWord(n)} as soon as the card is charged.</div>
            </section>

            {/* after you key it */}
            <section className="dc-card dc-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h3 className="dc-h3" style={{ fontSize: 17 }}>After you key it into your card machine</h3>
              <label className="dc-flabel" style={{ maxWidth: 280 }}><span>Approval code from the receipt</span><input className="dc-field" value={approval} onChange={(e) => { setApproval(e.target.value); }} /></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <button
                  type="button"
                  className="dc-btn dc-btn-primary dc-btn-lg"
                  disabled={!approvalTyped}
                  aria-describedby={reasonId}
                  onClick={() => { onMarkCharged(approval.trim()); }}
                >
                  Mark as charged
                </button>
                <button type="button" className="dc-btn" onClick={onCouldntCharge}>Couldn’t charge it</button>
              </div>
              <span id={reasonId} className="dc-meta">
                {approvalTyped
                  ? `ELVA posts ${n === 1 ? 'the payment' : `the ${String(n)} payments`} as soon as you mark it charged.`
                  : `Enter the approval code first. Then ELVA posts ${n === 1 ? 'the payment' : `the ${String(n)} payments`}.`}
              </span>
              <div className="dc-notice">Later: ELVA will charge cards itself, through your own card processor.</div>
            </section>

            {/* the fax */}
            <CardFax card={card} onOpenFax={onOpenFax} />

            {/* EFT insight */}
            {card.insight ? (
              <section className="dc-card dc-didcard" style={{ borderColor: '#b7dcd8' }}>
                <div className="dc-didhead" style={{ background: 'var(--psoft)' }}><h3 className="dc-h3" style={{ color: 'var(--psoft-fg)' }}>{card.insight.text}</h3></div>
                <div className="dc-didbody">
                  <div style={{ fontSize: 14.5, color: 'var(--fg2)' }}>Most payers will pay by direct deposit (EFT) instead, with no fee, if the practice asks.</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                    <button type="button" className="dc-btn" onClick={() => { onAskEft(card.insight?.payer ?? card.payer); }}>Ask {card.insight.payer} to pay by EFT</button>
                    <span className="dc-meta">ELVA calls {card.insight.payer} and fills in its form. Anything needing a signature comes to you.</span>
                  </div>
                </div>
              </section>
            ) : null}
          </>
        )}
      </div>
    </main>
  );
}
